import Animate from './animate';
import errorHandler from './error-handler';

export default class ProductList {
    constructor($el) {
        this.$el = $el;
        this.$loader = document.querySelector('[data-loader]');
        this.$form = this.$el.querySelector('[data-product-list-filters]');
        this.currentRequestUrl = `${window.location.pathname}${window.location.search}`;
        this._handleChange = this._handleChange.bind(this);
        this._handleClick = this._handleClick.bind(this);
        this._handlePopState = this._handlePopState.bind(this);

        this.$el.addEventListener('change', this._handleChange);
        this.$el.addEventListener('click', this._handleClick);
        if (this.$form) {
            window.addEventListener('popstate', this._handlePopState);
        }
    }

    _handleChange(event) {
        if (!event.target.matches('[data-product-list-filter]')) {
            return;
        }

        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
            const params = this._getFormParams();
            this._loadResults(params, this._getPublicUrl(params));
        }, 200);
    }

    _handleClick(event) {
        const $toggle = event.target.closest('[data-product-list-categories-toggle]');
        if ($toggle && this.$el.contains($toggle)) {
            this._toggleCategories($toggle);
            return;
        }

        const $page = event.target.closest('[data-product-list-page]');
        if (!$page || !this.$el.contains($page)) {
            return;
        }

        event.preventDefault();
        const url = new URL($page.href);
        const params = Object.fromEntries(url.searchParams);
        this._loadResults(params, url, true);
    }

    _handlePopState() {
        const requestUrl = `${window.location.pathname}${window.location.search}`;
        if (requestUrl === this.currentRequestUrl) {
            return;
        }

        this.currentRequestUrl = requestUrl;
        const params = Object.fromEntries(new URLSearchParams(window.location.search));
        this._syncFilters(params);
        this._loadResults(params);
    }

    _toggleCategories($toggle) {
        const $categories = this.$el.querySelector('[data-product-list-categories]');
        const isOpen = $categories.classList.toggle('product-list__categories-wrapper--open');
        const $icon = $toggle.querySelector('i');
        $icon.classList.toggle('icon-down', !isOpen);
        $icon.classList.toggle('icon-up', isOpen);
    }

    _getFormParams() {
        const formData = new FormData(this._getForm());
        return Object.fromEntries(
            Array.from(formData.entries()).filter(([, value]) => value)
        );
    }

    _getPublicUrl(params) {
        const url = new URL(this._getForm().action);
        const publicParams = {...params};
        delete publicParams.category;
        delete publicParams.product;
        url.search = new URLSearchParams(publicParams).toString();
        return url;
    }

    async _loadResults(params, publicUrl = null, scrollToTop = false) {
        this._setLoading(true);
        if (this.abortController) {
            this.abortController.abort();
        }

        const abortController = new AbortController();
        this.abortController = abortController;

        try {
            const response = await fetch(this._getAjaxUrl(params), {signal: abortController.signal});
            if (!response.ok) {
                throw {code: response.status, message: response.statusText};
            }

            this._replaceResults(await response.text());
            if (publicUrl) {
                window.history.pushState({}, '', publicUrl);
                this.currentRequestUrl = `${publicUrl.pathname}${publicUrl.search}`;
            }
            if (scrollToTop) {
                window.scrollTo(0, 0);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                errorHandler.handleError(error);
            }
        } finally {
            if (this.abortController === abortController) {
                this._setLoading(false);
            }
        }
    }

    _getAjaxUrl(params) {
        const blockId = this.$el.dataset.blockId;
        const url = new URL(`${baseHref}ajax/product-list/${blueprintAlias}/${blockId}`);
        const ajaxParams = {...params, category: this._getForm().dataset.categoryId};
        Object.entries(ajaxParams).forEach(([name, value]) => url.searchParams.set(name, value));
        return url;
    }

    _replaceResults(html) {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const $newResults = documentFragment.querySelector('[data-product-list-results]');
        const $currentResults = this.$el.querySelector('[data-product-list-results]');
        if (!$newResults || !$currentResults) {
            throw {code: 500, message: 'Product list results could not be rendered'};
        }

        $currentResults.replaceWith($newResults);
        new Animate($newResults);
        $newResults.querySelectorAll('[data-animate]').forEach($item => new Animate($item));
    }

    _syncFilters(params) {
        this.$el.querySelectorAll('[data-product-list-filter]').forEach($filter => {
            const defaultValue = $filter.name === 'sort'
                ? 'category_asc'
                : ($filter.dataset.defaultValue || '');
            $filter.value = params[$filter.name] || defaultValue;
        });
    }

    _setLoading(isLoading) {
        if (this.$loader) {
            this.$loader.classList.toggle('loader--loading', isLoading);
        }
    }

    _getForm() {
        return this.$form;
    }
}
